/**
 * HostPoint POS - Enhanced Printer Manager
 * Supports multiple printers: iMin SDK, Bluetooth, Windows native printers
 */

import iminPrinter from './imin-printer-sdk';

// ESC/POS Command Constants
const ESC = 0x1B;
const GS = 0x1D;

const COMMANDS = {
  // Initialization
  INIT: [ESC, 0x40],
  
  // Print density/emphasis (crucial for clear printing)
  EMPHASIS_ON: [ESC, 0x47, 0x01],   // Double-strike mode ON (darker printing)
  EMPHASIS_OFF: [ESC, 0x47, 0x00],  // Double-strike mode OFF
  PRINT_DENSITY: (n) => [GS, 0x7C, n], // Set print density (0-8, where 4 is normal)
  
  // Alignment
  ALIGN_LEFT: [ESC, 0x61, 0x00],
  ALIGN_CENTER: [ESC, 0x61, 0x01],
  ALIGN_RIGHT: [ESC, 0x61, 0x02],
  
  // Text styling
  BOLD_ON: [ESC, 0x45, 0x01],
  BOLD_OFF: [ESC, 0x45, 0x00],
  UNDERLINE_ON: [ESC, 0x2D, 0x01],
  UNDERLINE_OFF: [ESC, 0x2D, 0x00],
  INVERSE_ON: [GS, 0x42, 0x01],
  INVERSE_OFF: [GS, 0x42, 0x00],
  
  // Character size
  NORMAL_SIZE: [GS, 0x21, 0x00],
  DOUBLE_HEIGHT: [GS, 0x21, 0x10],
  DOUBLE_WIDTH: [GS, 0x21, 0x20],
  DOUBLE_SIZE: [GS, 0x21, 0x30],
  QUAD_SIZE: [GS, 0x21, 0x33],
  
  // Line spacing
  DEFAULT_LINE_SPACING: [ESC, 0x32],
  SET_LINE_SPACING: (n) => [ESC, 0x33, n],
  
  // Paper control
  FEED_LINE: [0x0A],
  FEED_LINES: (n) => [ESC, 0x64, n],
  CUT_FULL: [GS, 0x56, 0x00],
  CUT_PARTIAL: [GS, 0x56, 0x01],
  CUT_FEED_FULL: [GS, 0x56, 0x41, 0x03],
  CUT_FEED_PARTIAL: [GS, 0x56, 0x42, 0x03],
  
  // Beeper (if supported)
  BEEP: [ESC, 0x42, 0x03, 0x02], // 3 times, 200ms
  
  // Cash drawer
  OPEN_DRAWER: [ESC, 0x70, 0x00, 0x19, 0xFA],
  
  // Character set
  CHARSET_PC437: [ESC, 0x74, 0x00],
  CHARSET_PC850: [ESC, 0x74, 0x02],
};

// Common Bluetooth printer service UUIDs
const PRINTER_SERVICE_UUIDS = [
  '000018f0-0000-1000-8000-00805f9b34fb', // Generic printer service
  '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip BLE UART
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Nordic UART Service
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART
  '0000ffe0-0000-1000-8000-00805f9b34fb', // HM-10 / iMin internal printer
  '0000ff00-0000-1000-8000-00805f9b34fb', // Generic
  '00001101-0000-1000-8000-00805f9b34fb', // SPP (Serial Port Profile)
  '0000fff0-0000-1000-8000-00805f9b34fb', // Custom printer service
  '0000fee7-0000-1000-8000-00805f9b34fb', // iMin printer service
];

// iMin internal printer identifiers (Bluetooth device name is "BluetoothPrinter")
const IMIN_PRINTER_NAMES = [
  'BluetoothPrinter', // Primary iMin internal printer name
  'InnerPrinter',
  'iMin',
  'Printer',
  'POS-',
  'D4',
  'Swift',
  'Falcon',
  'M2',
  'IMIN'
];

// Saved printers storage key
const SAVED_PRINTERS_KEY = 'hostpoint_saved_printers';
const PRINTER_CONFIG_KEY = 'hostpoint_printer_config';

class BluetoothPrinterManager {
  constructor() {
    this.connectedPrinters = new Map(); // deviceId -> PrinterConnection
    this.savedPrinters = this.loadSavedPrinters();
    this.printerConfig = this.loadPrinterConfig();
    this.onConnectionChange = null; // Callback for connection status changes
    this.iminSDKAvailable = iminPrinter.isAvailable();
  }

  /**
   * Load full printer configuration from localStorage
   */
  loadPrinterConfig() {
    try {
      const saved = localStorage.getItem(PRINTER_CONFIG_KEY);
      return saved ? JSON.parse(saved) : {
        autoConnect: true,
        defaultReceiptPrinter: null,
        defaultKitchenPrinter: null,
        defaultBarPrinter: null,
        printerProfiles: {} // printerId -> { paperWidth, printDensity, type, destinations[], autoConnect }
      };
    } catch {
      return { autoConnect: true, printerProfiles: {} };
    }
  }

  /**
   * Save full printer configuration to localStorage
   */
  savePrinterConfig() {
    localStorage.setItem(PRINTER_CONFIG_KEY, JSON.stringify(this.printerConfig));
  }

  /**
   * Get printer profile (paper width, density, assigned destinations)
   */
  getPrinterProfile(printerId) {
    return this.printerConfig.printerProfiles[printerId] || null;
  }

  /**
   * Update printer profile settings
   */
  updatePrinterProfile(printerId, settings) {
    this.printerConfig.printerProfiles[printerId] = {
      ...(this.printerConfig.printerProfiles[printerId] || {}),
      ...settings,
      updatedAt: new Date().toISOString()
    };
    this.savePrinterConfig();
  }

  /**
   * Set default printer for a destination type
   */
  setDefaultPrinter(type, printerId) {
    const key = `default${type.charAt(0).toUpperCase() + type.slice(1)}Printer`;
    this.printerConfig[key] = printerId;
    this.savePrinterConfig();
  }

  /**
   * Get default printer for a destination type
   */
  getDefaultPrinter(type) {
    const key = `default${type.charAt(0).toUpperCase() + type.slice(1)}Printer`;
    return this.printerConfig[key] || null;
  }

  /**
   * Set auto-connect preference
   */
  setAutoConnect(enabled) {
    this.printerConfig.autoConnect = enabled;
    this.savePrinterConfig();
  }

  /**
   * Get full printer config for UI display
   */
  getFullConfig() {
    return { ...this.printerConfig };
  }

  /**
   * Check if iMin SDK is available (IminPrintInstance global)
   */
  hasIminSDK() {
    return iminPrinter.isAvailable();
  }

  /**
   * Get iMin SDK instance
   */
  getIminSDK() {
    return iminPrinter;
  }

  /**
   * Check if Web Bluetooth is supported
   */
  isSupported() {
    return ('bluetooth' in navigator) || this.hasIminSDK();
  }

  /**
   * Check if this is likely an iMin device
   */
  isIminDevice() {
    if (this.hasIminSDK()) return true;
    
    // Check user agent for iMin patterns
    const ua = navigator.userAgent.toLowerCase();
    const iminPatterns = ['imin', 'd4', 'swift', 'falcon', 'swan', 'm2-'];
    return iminPatterns.some(p => ua.includes(p));
  }

  /**
   * Print using iMin SDK (native method - preferred for iMin devices)
   */
  async printViaIminSDK(content, options = {}) {
    if (!this.hasIminSDK()) {
      throw new Error('iMin SDK not available');
    }

    const sdk = this.getIminSDK();
    
    // Initialize if needed
    if (!sdk.isInitialized) {
      await sdk.initPrinter();
    }

    // If content is a receipt object, use printReceipt
    if (typeof content === 'object' && content.items) {
      return await sdk.printReceipt(content);
    }

    // Otherwise print as text
    if (typeof content === 'string') {
      sdk.setAlignment(options.alignment || 'left');
      sdk.setTextSize(options.fontSize || 24);
      sdk.printText(content);
      sdk.feedPaper(80);
      if (options.cut !== false) {
        sdk.cutPaper();
      }
      return { success: true, method: 'imin-sdk' };
    }

    throw new Error('Invalid content type');
  }

  /**
   * Print test page using iMin SDK
   */
  async testPrintIminSDK() {
    if (!this.hasIminSDK()) {
      throw new Error('iMin SDK not available');
    }

    return await this.getIminSDK().printTestPage();
  }

  /**
   * Load saved printers from localStorage
   */
  loadSavedPrinters() {
    try {
      const saved = localStorage.getItem(SAVED_PRINTERS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  }

  /**
   * Save printer to localStorage with full profile
   */
  savePrinter(printer) {
    const existing = this.savedPrinters.find(p => p.id === printer.id);
    if (!existing) {
      this.savedPrinters.push({
        id: printer.id,
        name: printer.name,
        type: printer.type || 'receipt',
        paperWidth: printer.paperWidth || 58,
        lastConnected: new Date().toISOString()
      });
    } else {
      existing.lastConnected = new Date().toISOString();
    }
    localStorage.setItem(SAVED_PRINTERS_KEY, JSON.stringify(this.savedPrinters));
    
    // Also update profile if not exists
    if (!this.printerConfig.printerProfiles[printer.id]) {
      this.updatePrinterProfile(printer.id, {
        paperWidth: printer.paperWidth || 58,
        printDensity: 6,
        type: printer.type || 'receipt',
        destinations: [printer.type || 'receipt'],
        autoConnect: true,
        name: printer.name
      });
    }
  }

  /**
   * Remove saved printer
   */
  removeSavedPrinter(printerId) {
    this.savedPrinters = this.savedPrinters.filter(p => p.id !== printerId);
    localStorage.setItem(SAVED_PRINTERS_KEY, JSON.stringify(this.savedPrinters));
  }

  /**
   * Get list of saved printers
   */
  getSavedPrinters() {
    return this.savedPrinters;
  }

  /**
   * Check if device is likely an iMin internal printer
   */
  isIminPrinter(deviceName) {
    if (!deviceName) return false;
    const name = deviceName.toLowerCase();
    return IMIN_PRINTER_NAMES.some(p => name.includes(p.toLowerCase()));
  }

  /**
   * Connect to iMin internal printer via SDK
   * This creates a virtual printer connection that uses the native SDK
   */
  async connectIminInternal(options = {}) {
    if (!this.hasIminSDK()) {
      throw new Error('iMin SDK not available. Make sure you are running on an iMin device with iMinPrinterPlugin installed.');
    }

    // Initialize iMin SDK
    const sdk = this.getIminSDK();
    await sdk.initPrinter();
    
    // Get printer status
    const status = await sdk.getStatusText();

    const virtualConnection = {
      device: null,
      server: null,
      characteristic: null,
      isConnected: true,
      isVirtual: true,
      isIminInternal: true,
      useIminSDK: true,
      name: `iMin Internal Printer (${sdk.deviceModel?.name || 'Built-in'})`,
      id: 'imin-internal-printer',
      type: options.type || 'receipt',
      paperWidth: options.paperWidth || 80,
      status: status
    };

    this.connectedPrinters.set(virtualConnection.id, virtualConnection);
    this.savePrinter(virtualConnection);
    
    this.onConnectionChange?.({ type: 'connected', printer: virtualConnection });

    return virtualConnection;
  }

  /**
   * Print via browser's native print dialog (for Windows/Desktop)
   * This uses window.print() to show system printer selection
   */
  async printViaBrowser(content, options = {}) {
    const { title = 'Print', paperWidth = 80 } = options;
    
    // Convert content to printable HTML
    let printContent = content;
    if (typeof content === 'object' && content.items) {
      // Receipt object - convert to HTML
      printContent = this.receiptToHtml(content, paperWidth);
    }

    // Create print window
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) {
      throw new Error('Could not open print window. Please allow popups.');
    }

    const paperWidthMm = paperWidth === 58 ? '58mm' : '80mm';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <style>
          @page {
            size: ${paperWidthMm} auto;
            margin: 0;
          }
          @media print {
            body { margin: 0; padding: 0; }
          }
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            width: ${paperWidth === 58 ? '48mm' : '72mm'};
            margin: 0 auto;
            padding: 2mm;
          }
          .center { text-align: center; }
          .right { text-align: right; }
          .bold { font-weight: bold; }
          .large { font-size: 16px; }
          .divider { border-top: 1px dashed #000; margin: 4px 0; }
          .row { display: flex; justify-content: space-between; }
          pre { margin: 0; white-space: pre-wrap; }
        </style>
      </head>
      <body>
        ${typeof printContent === 'string' ? `<pre>${printContent}</pre>` : printContent}
      </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load then print
    await new Promise(resolve => setTimeout(resolve, 250));
    printWindow.focus();
    printWindow.print();
    
    // Close after a delay to allow print dialog
    setTimeout(() => {
      printWindow.close();
    }, 1000);

    return { success: true, method: 'browser-print' };
  }

  /**
   * Convert receipt object to printable HTML
   */
  receiptToHtml(receipt, paperWidth = 80) {
    const {
      storeName = 'HostPoint POS',
      storeAddress = '',
      invoiceNumber = '',
      date = new Date().toLocaleString(),
      items = [],
      subtotal = 0,
      tax = 0,
      total = 0,
      paymentMethod = 'Cash',
      cashier = '',
      footer = 'Thank you!'
    } = receipt;

    const divider = '<div class="divider"></div>';
    const lineWidth = paperWidth === 58 ? 32 : 48;
    
    let html = `
      <div class="center large bold">${storeName}</div>
      ${storeAddress ? `<div class="center">${storeAddress}</div>` : ''}
      ${divider}
      ${invoiceNumber ? `<div>Invoice: ${invoiceNumber}</div>` : ''}
      <div>Date: ${date}</div>
      ${cashier ? `<div>Cashier: ${cashier}</div>` : ''}
      ${divider}
    `;

    // Items
    for (const item of items) {
      const name = item.name || 'Item';
      const qty = item.quantity || 1;
      const itemTotal = item.total || (qty * (item.price || 0));
      html += `<div class="row"><span>${qty}x ${name}</span><span>${itemTotal.toFixed(2)}</span></div>`;
    }

    html += `
      ${divider}
      <div class="row"><span>Subtotal:</span><span>${subtotal.toFixed(2)}</span></div>
      ${tax > 0 ? `<div class="row"><span>Tax:</span><span>${tax.toFixed(2)}</span></div>` : ''}
      <div class="row bold large"><span>TOTAL:</span><span>${total.toFixed(2)}</span></div>
      <div>Payment: ${paymentMethod}</div>
      ${divider}
      <div class="center">${footer}</div>
    `;

    return html;
  }

  /**
   * Scan for and connect to a Bluetooth printer
   * For iMin devices, Bluetooth printer name is "BluetoothPrinter"
   */
  async scanAndConnect(options = {}) {
    // If iMin SDK is available and user wants internal printer, use SDK
    if (options.useIminSDK && this.hasIminSDK()) {
      return this.connectIminInternal(options);
    }

    if (!('bluetooth' in navigator)) {
      // Fall back to iMin SDK if Bluetooth not available
      if (this.hasIminSDK()) {
        return this.connectIminInternal(options);
      }
      throw new Error('Web Bluetooth is not supported in this browser. Use Chrome on Android or a compatible desktop browser.');
    }

    try {
      
      // Build filters for printer discovery
      // For iMin internal Bluetooth printer, the device name is "BluetoothPrinter"
      const filters = [];
      
      // Add common printer name patterns
      const namePatterns = [
        'BluetoothPrinter',  // iMin internal Bluetooth printer
        'InnerPrinter',
        'Printer',
        'POS',
        'iMin',
        'IMIN',
        'D4',
        'Swift',
        'Falcon',
        'RPP',      // Many receipt printers
        'SPP',      // Serial Port Profile printers
        'Thermal',
        'GOOJPRT',
        'MTP',
        'XP-',
        'PT-',
      ];
      
      // Add name prefix filters
      namePatterns.forEach(name => {
        filters.push({ namePrefix: name });
      });
      
      let device;
      try {
        // Try filtered discovery first - this gives a better UI experience
        device = await navigator.bluetooth.requestDevice({
          filters: filters,
          optionalServices: PRINTER_SERVICE_UUIDS
        });
      } catch (filterError) {
        // If filtered fails (no matching devices or user cancelled), try accept all
        if (filterError.name === 'NotFoundError' || filterError.message?.includes('cancelled')) {
          throw filterError; // Re-throw if user cancelled
        }
        
        device = await navigator.bluetooth.requestDevice({
          acceptAllDevices: true,
          optionalServices: PRINTER_SERVICE_UUIDS
        });
      }


      // Check if this is an iMin internal printer
      if (this.isIminPrinter(device.name)) {
        options.isIminInternal = true;
        options.type = options.type || 'receipt';
        options.paperWidth = options.paperWidth || 80; // iMin D4 uses 80mm paper
      }

      return await this.connectToDevice(device, options);
    } catch (error) {
      
      if (error.name === 'NotFoundError') {
        throw new Error('No printer selected. Please select a Bluetooth printer from the list.');
      }
      if (error.message?.includes('cancelled') || error.name === 'NotAllowedError') {
        throw new Error('Bluetooth scan was cancelled.');
      }
      if (error.message?.includes('User denied')) {
        throw new Error('Bluetooth permission denied. Please allow Bluetooth access.');
      }
      throw error;
    }
  }

  /**
   * Connect to a specific Bluetooth device
   */
  async connectToDevice(device, options = {}) {
    const connection = {
      device,
      server: null,
      characteristic: null,
      isConnected: false,
      name: device.name || 'Unknown Printer',
      id: device.id,
      type: options.type || 'receipt',
      paperWidth: options.paperWidth || 58,
      isIminInternal: options.isIminInternal || this.isIminPrinter(device.name)
    };

    try {
      
      // Connect to GATT server with timeout
      const connectPromise = device.gatt.connect();
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Connection timeout after 15 seconds')), 15000)
      );
      
      connection.server = await Promise.race([connectPromise, timeoutPromise]);
      
      // Small delay to let the connection stabilize
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Get primary services
      let services;
      try {
        services = await connection.server.getPrimaryServices();
      } catch (serviceError) {
        throw new Error('Could not discover printer services. Please try again.');
      }
      
      // Priority order for finding writable characteristic
      const priorityServiceUUIDs = connection.isIminInternal ? [
        '0000ffe0-0000-1000-8000-00805f9b34fb', // HM-10 / iMin internal
        '0000fee7-0000-1000-8000-00805f9b34fb', // iMin specific
        '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip BLE UART
        '0000fff0-0000-1000-8000-00805f9b34fb', // Generic
        '00001101-0000-1000-8000-00805f9b34fb', // SPP
      ] : [
        '000018f0-0000-1000-8000-00805f9b34fb', // Generic printer
        '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Microchip BLE UART
        'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Nordic UART
        '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART
        '0000ffe0-0000-1000-8000-00805f9b34fb', // HM-10
        '0000fff0-0000-1000-8000-00805f9b34fb', // Generic
      ];
      
      // Try priority services first
      for (const priorityUUID of priorityServiceUUIDs) {
        try {
          const service = services.find(s => s.uuid === priorityUUID);
          if (service) {
            const characteristics = await service.getCharacteristics();
            for (const char of characteristics) {
              if (char.properties.write || char.properties.writeWithoutResponse) {
                connection.characteristic = char;
                break;
              }
            }
          }
          if (connection.characteristic) break;
        } catch (e) {
        }
      }
      
      // If not found in priority services, search all services
      if (!connection.characteristic) {
        for (const service of services) {
          try {
            const characteristics = await service.getCharacteristics();
            for (const char of characteristics) {
              const props = [];
              if (char.properties.write) props.push('write');
              if (char.properties.writeWithoutResponse) props.push('writeWithoutResponse');
              if (char.properties.read) props.push('read');
              if (char.properties.notify) props.push('notify');
              
              if (char.properties.write || char.properties.writeWithoutResponse) {
                connection.characteristic = char;
                break;
              }
            }
            if (connection.characteristic) break;
          } catch (e) {
          }
        }
      }

      if (!connection.characteristic) {
        // Disconnect since we couldn't find a writable characteristic
        if (connection.server?.connected) {
          device.gatt.disconnect();
        }
        throw new Error('No writable characteristic found. This device may not be a compatible printer or may require native pairing first.');
      }

      connection.isConnected = true;
      this.connectedPrinters.set(device.id, connection);
      
      // Save printer for future
      this.savePrinter(connection);

      // Listen for disconnection
      device.addEventListener('gattserverdisconnected', () => {
        connection.isConnected = false;
        this.connectedPrinters.delete(device.id);
        this.onConnectionChange?.({ type: 'disconnected', printer: connection });
      });

      this.onConnectionChange?.({ type: 'connected', printer: connection });

      return connection;
    } catch (error) {
      connection.isConnected = false;
      
      // Make sure to disconnect on error
      try {
        if (device.gatt?.connected) {
          device.gatt.disconnect();
        }
      } catch (e) {
        // Ignore disconnect errors
      }
      
      throw error;
    }
  }

  /**
   * Reconnect to a previously saved printer
   */
  async reconnect(printerId) {
    const saved = this.savedPrinters.find(p => p.id === printerId);
    if (!saved) {
      throw new Error('Printer not found in saved list');
    }

    // Note: Web Bluetooth doesn't support reconnecting without user gesture
    // This will open the device picker pre-filtered
    return this.scanAndConnect({
      type: saved.type,
      paperWidth: saved.paperWidth
    });
  }

  /**
   * Disconnect a printer
   */
  disconnect(printerId) {
    const connection = this.connectedPrinters.get(printerId);
    if (connection && connection.device?.gatt?.connected) {
      connection.device.gatt.disconnect();
    }
    this.connectedPrinters.delete(printerId);
  }

  /**
   * Disconnect all printers
   */
  disconnectAll() {
    for (const [id] of this.connectedPrinters) {
      this.disconnect(id);
    }
  }

  /**
   * Get connected printers
   */
  getConnectedPrinters() {
    return Array.from(this.connectedPrinters.values());
  }

  /**
   * Check if any printer is connected
   */
  hasConnectedPrinter() {
    return this.connectedPrinters.size > 0;
  }

  /**
   * Get a connected printer by type
   */
  getPrinterByType(type) {
    for (const connection of this.connectedPrinters.values()) {
      if (connection.type === type) {
        return connection;
      }
    }
    return null;
  }

  /**
   * Write raw bytes to a printer
   */
  async writeRaw(printerId, data) {
    const connection = this.connectedPrinters.get(printerId);
    if (!connection?.isConnected) {
      throw new Error('Printer not connected');
    }

    const uint8Array = new Uint8Array(data);
    
    // Split into chunks (BLE MTU is typically 20-512 bytes)
    const chunkSize = 100;
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
      const chunk = uint8Array.slice(i, i + chunkSize);
      
      try {
        if (connection.characteristic.properties.writeWithoutResponse) {
          await connection.characteristic.writeValueWithoutResponse(chunk);
        } else {
          await connection.characteristic.writeValue(chunk);
        }
      } catch (error) {
        // If write fails, try reconnecting
        if (error.message.includes('GATT')) {
          connection.isConnected = false;
          throw new Error('Printer disconnected during print. Please reconnect.');
        }
        throw error;
      }
      
      // Small delay between chunks for printer buffer
      await new Promise(resolve => setTimeout(resolve, 30));
    }
  }

  /**
   * Convert text to bytes
   */
  textToBytes(text) {
    const encoder = new TextEncoder();
    return [...encoder.encode(text)];
  }

  /**
   * Format currency
   */
  formatCurrency(amount, symbol = 'KSh') {
    return `${symbol} ${(amount || 0).toLocaleString('en-KE', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  }

  /**
   * Create a line for receipt (fixed width)
   */
  formatReceiptLine(left, right, width = 32) {
    const space = width - left.length - right.length;
    if (space < 1) {
      return left.substring(0, width - right.length - 1) + ' ' + right;
    }
    return left + ' '.repeat(space) + right;
  }

  /**
   * Create centered text
   */
  centerText(text, width = 32) {
    const padding = Math.max(0, Math.floor((width - text.length) / 2));
    return ' '.repeat(padding) + text;
  }

  /**
   * Create a divider line
   */
  dividerLine(char = '-', width = 32) {
    return char.repeat(width);
  }

  /**
   * Print a receipt
   */
  async printReceipt(printerId, receiptData) {
    const connection = this.connectedPrinters.get(printerId);
    if (!connection) {
      throw new Error('Printer not connected');
    }

    const width = connection.paperWidth === 80 ? 48 : 32;
    const commands = [];
    
    // Get print density from receipt settings (default to 6 for dark/clear printing)
    const printDensity = receiptData.settings?.print_density || 6;

    // Initialize printer
    commands.push(...COMMANDS.INIT);
    commands.push(...COMMANDS.CHARSET_PC437);
    
    // Enable emphasis/double-strike for clearer printing
    commands.push(...COMMANDS.EMPHASIS_ON);
    
    // Set print density based on settings
    commands.push(...COMMANDS.PRINT_DENSITY(printDensity));

    // Header - Business name
    commands.push(...COMMANDS.ALIGN_CENTER);
    commands.push(...COMMANDS.DOUBLE_SIZE);
    commands.push(...COMMANDS.BOLD_ON);
    commands.push(...this.textToBytes(receiptData.businessName || 'HostPoint POS'));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...COMMANDS.NORMAL_SIZE);
    commands.push(...COMMANDS.BOLD_OFF);

    // Address and contact
    if (receiptData.address) {
      commands.push(...this.textToBytes(receiptData.address));
      commands.push(...COMMANDS.FEED_LINE);
    }
    if (receiptData.phone) {
      commands.push(...this.textToBytes(`Tel: ${receiptData.phone}`));
      commands.push(...COMMANDS.FEED_LINE);
    }
    if (receiptData.email) {
      commands.push(...this.textToBytes(receiptData.email));
      commands.push(...COMMANDS.FEED_LINE);
    }

    // Divider
    commands.push(...this.textToBytes(this.dividerLine('=', width)));
    commands.push(...COMMANDS.FEED_LINE);

    // Receipt info
    commands.push(...COMMANDS.ALIGN_LEFT);
    commands.push(...this.textToBytes(`Receipt: ${receiptData.receiptNumber || 'N/A'}`));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...this.textToBytes(`Date: ${receiptData.date || new Date().toLocaleString()}`));
    commands.push(...COMMANDS.FEED_LINE);

    if (receiptData.tableNumber) {
      commands.push(...this.textToBytes(`Table: ${receiptData.tableNumber}`));
      commands.push(...COMMANDS.FEED_LINE);
    }
    if (receiptData.customerName && receiptData.customerName !== 'Walk-in') {
      commands.push(...this.textToBytes(`Customer: ${receiptData.customerName}`));
      commands.push(...COMMANDS.FEED_LINE);
    }
    if (receiptData.servedBy) {
      commands.push(...this.textToBytes(`Served by: ${receiptData.servedBy}`));
      commands.push(...COMMANDS.FEED_LINE);
    }

    // Offline indicator
    if (receiptData.isOffline) {
      commands.push(...COMMANDS.BOLD_ON);
      commands.push(...this.textToBytes('*** OFFLINE ORDER ***'));
      commands.push(...COMMANDS.BOLD_OFF);
      commands.push(...COMMANDS.FEED_LINE);
    }

    // Items header
    commands.push(...this.textToBytes(this.dividerLine('-', width)));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...COMMANDS.BOLD_ON);
    
    const itemHeader = width === 48 
      ? this.formatReceiptLine('Item', 'Qty    Amount', width)
      : 'Item              Qty   Amount';
    commands.push(...this.textToBytes(itemHeader));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...COMMANDS.BOLD_OFF);
    commands.push(...this.textToBytes(this.dividerLine('-', width)));
    commands.push(...COMMANDS.FEED_LINE);

    // Items
    for (const item of (receiptData.items || [])) {
      const itemTotal = (item.price || 0) * (item.quantity || 1);
      const qty = item.quantity.toString();
      const amount = this.formatCurrency(itemTotal);
      
      // Item name (may wrap)
      const nameMaxWidth = width - 12;
      let itemName = item.name || 'Item';
      
      if (itemName.length > nameMaxWidth) {
        // Print name on first line
        commands.push(...this.textToBytes(itemName.substring(0, nameMaxWidth)));
        commands.push(...COMMANDS.FEED_LINE);
        // Print qty and amount on second line, right-aligned
        const qtyAmount = `${qty.padStart(3)} ${amount.padStart(12)}`;
        commands.push(...this.textToBytes(' '.repeat(width - qtyAmount.length) + qtyAmount));
      } else {
        const line = this.formatReceiptLine(itemName, `${qty.padStart(3)} ${amount.padStart(12)}`, width);
        commands.push(...this.textToBytes(line));
      }
      commands.push(...COMMANDS.FEED_LINE);

      // Only show add-ons/modifiers that have a price (billable items only on receipt)
      if (item.selected_options?.length > 0) {
        for (const opt of item.selected_options) {
          if (opt.price && opt.price > 0) {
            const modLine = `  + ${opt.option_name} (+${this.formatCurrency(opt.price)})`;
            commands.push(...this.textToBytes(modLine));
            commands.push(...COMMANDS.FEED_LINE);
          }
        }
      }
      
      // Meal slot selections with price adjustments only
      if (item.meal_selections?.length > 0) {
        for (const slot of item.meal_selections) {
          if (slot.price_adjustment && slot.price_adjustment > 0) {
            const slotLine = `  ${slot.slot_name}: ${slot.product_name} (+${this.formatCurrency(slot.price_adjustment)})`;
            commands.push(...this.textToBytes(slotLine));
            commands.push(...COMMANDS.FEED_LINE);
          }
        }
      }
    }

    // Totals section
    commands.push(...this.textToBytes(this.dividerLine('-', width)));
    commands.push(...COMMANDS.FEED_LINE);

    // Subtotal
    commands.push(...this.textToBytes(
      this.formatReceiptLine('Subtotal:', this.formatCurrency(receiptData.subtotal), width)
    ));
    commands.push(...COMMANDS.FEED_LINE);

    // Tax
    if (receiptData.tax) {
      const taxLabel = receiptData.taxRate ? `Tax (${receiptData.taxRate}%):` : 'Tax:';
      commands.push(...this.textToBytes(
        this.formatReceiptLine(taxLabel, this.formatCurrency(receiptData.tax), width)
      ));
      commands.push(...COMMANDS.FEED_LINE);
    }

    // Discount
    if (receiptData.discount && receiptData.discount > 0) {
      commands.push(...this.textToBytes(
        this.formatReceiptLine('Discount:', `-${this.formatCurrency(receiptData.discount)}`, width)
      ));
      commands.push(...COMMANDS.FEED_LINE);
    }

    // Total
    commands.push(...this.textToBytes(this.dividerLine('=', width)));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...COMMANDS.BOLD_ON);
    commands.push(...COMMANDS.DOUBLE_HEIGHT);
    commands.push(...this.textToBytes(
      this.formatReceiptLine('TOTAL:', this.formatCurrency(receiptData.total), width)
    ));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...COMMANDS.NORMAL_SIZE);
    commands.push(...COMMANDS.BOLD_OFF);

    // Payment info
    commands.push(...this.textToBytes(this.dividerLine('-', width)));
    commands.push(...COMMANDS.FEED_LINE);
    
    const paymentMethod = (receiptData.paymentMethod || 'Cash').toUpperCase();
    commands.push(...this.textToBytes(
      this.formatReceiptLine('Payment:', paymentMethod, width)
    ));
    commands.push(...COMMANDS.FEED_LINE);

    if (receiptData.amountPaid) {
      commands.push(...this.textToBytes(
        this.formatReceiptLine('Amount Paid:', this.formatCurrency(receiptData.amountPaid), width)
      ));
      commands.push(...COMMANDS.FEED_LINE);
    }

    if (receiptData.change && receiptData.change > 0) {
      commands.push(...this.textToBytes(
        this.formatReceiptLine('Change:', this.formatCurrency(receiptData.change), width)
      ));
      commands.push(...COMMANDS.FEED_LINE);
    }

    if (receiptData.mpesaReceipt) {
      commands.push(...this.textToBytes(`M-Pesa Ref: ${receiptData.mpesaReceipt}`));
      commands.push(...COMMANDS.FEED_LINE);
    }

    // Loyalty points
    if (receiptData.pointsEarned && receiptData.pointsEarned > 0) {
      commands.push(...COMMANDS.FEED_LINE);
      commands.push(...COMMANDS.ALIGN_CENTER);
      commands.push(...COMMANDS.BOLD_ON);
      commands.push(...this.textToBytes(`+${receiptData.pointsEarned} Loyalty Points Earned!`));
      commands.push(...COMMANDS.BOLD_OFF);
      commands.push(...COMMANDS.FEED_LINE);
    }

    // Footer
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...COMMANDS.ALIGN_CENTER);
    commands.push(...this.textToBytes(receiptData.footer || 'Thank you for your visit!'));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...this.textToBytes('Please come again'));
    commands.push(...COMMANDS.FEED_LINE);

    // Timestamp
    commands.push(...this.textToBytes(new Date().toLocaleString()));
    commands.push(...COMMANDS.FEED_LINE);

    // Reprint notice
    if (receiptData.isReprint) {
      commands.push(...COMMANDS.FEED_LINE);
      commands.push(...COMMANDS.INVERSE_ON);
      commands.push(...this.textToBytes(' *** REPRINT *** '));
      commands.push(...COMMANDS.INVERSE_OFF);
      commands.push(...COMMANDS.FEED_LINE);
    }

    // Feed and cut
    commands.push(...COMMANDS.FEED_LINES(4));
    commands.push(...COMMANDS.CUT_FEED_PARTIAL);

    // Send to printer
    await this.writeRaw(printerId, commands);

    return { success: true, printerId };
  }

  /**
   * Print a kitchen/bar ticket
   */
  async printKitchenTicket(printerId, ticketData) {
    const connection = this.connectedPrinters.get(printerId);
    if (!connection) {
      throw new Error('Printer not connected');
    }

    const width = connection.paperWidth === 80 ? 48 : 32;
    const commands = [];

    // Initialize printer
    commands.push(...COMMANDS.INIT);
    
    // Enable emphasis/double-strike for clearer printing
    commands.push(...COMMANDS.EMPHASIS_ON);
    
    // Set higher print density for kitchen tickets (very important to be clear)
    const printDensity = ticketData.settings?.print_density || 7;
    commands.push(...COMMANDS.PRINT_DENSITY(printDensity));

    // Beep to alert kitchen staff
    commands.push(...COMMANDS.BEEP);

    // Header - Destination
    commands.push(...COMMANDS.ALIGN_CENTER);
    commands.push(...COMMANDS.INVERSE_ON);
    commands.push(...COMMANDS.DOUBLE_SIZE);
    commands.push(...this.textToBytes(` ${(ticketData.destination || 'KITCHEN').toUpperCase()} `));
    commands.push(...COMMANDS.INVERSE_OFF);
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...COMMANDS.NORMAL_SIZE);

    // Order info
    commands.push(...COMMANDS.ALIGN_LEFT);
    commands.push(...this.textToBytes(this.dividerLine('=', width)));
    commands.push(...COMMANDS.FEED_LINE);

    // Table number (large)
    commands.push(...COMMANDS.BOLD_ON);
    commands.push(...COMMANDS.QUAD_SIZE);
    commands.push(...this.textToBytes(`Table ${ticketData.tableNumber || 'N/A'}`));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...COMMANDS.NORMAL_SIZE);
    commands.push(...COMMANDS.BOLD_OFF);

    // Order details
    commands.push(...this.textToBytes(`Order: ${ticketData.orderNumber || 'N/A'}`));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...this.textToBytes(`Type: ${ticketData.orderType || 'Dine In'}`));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...this.textToBytes(`Time: ${new Date().toLocaleTimeString()}`));
    commands.push(...COMMANDS.FEED_LINE);
    if (ticketData.waiter) {
      commands.push(...this.textToBytes(`Waiter: ${ticketData.waiter}`));
      commands.push(...COMMANDS.FEED_LINE);
    }

    commands.push(...this.textToBytes(this.dividerLine('=', width)));
    commands.push(...COMMANDS.FEED_LINE);

    // Items (large and bold) - Kitchen tickets show ALL add-ons including non-priced instructions
    commands.push(...COMMANDS.BOLD_ON);
    for (const item of (ticketData.items || [])) {
      commands.push(...COMMANDS.DOUBLE_HEIGHT);
      commands.push(...this.textToBytes(`${item.quantity}x ${item.name}`));
      commands.push(...COMMANDS.FEED_LINE);
      commands.push(...COMMANDS.NORMAL_SIZE);
      
      // Prep options selected (e.g., "Well Done", "Medium") - shown regardless of price
      if (item.prep_options_selected?.length > 0) {
        commands.push(...COMMANDS.BOLD_OFF);
        commands.push(...this.textToBytes(`   > ${item.prep_options_selected.join(', ')}`));
        commands.push(...COMMANDS.BOLD_ON);
        commands.push(...COMMANDS.FEED_LINE);
      }
      
      // Free text prep instructions - shown regardless of price
      if (item.prep_instructions) {
        commands.push(...COMMANDS.BOLD_OFF);
        commands.push(...this.textToBytes(`   * ${item.prep_instructions}`));
        commands.push(...COMMANDS.BOLD_ON);
        commands.push(...COMMANDS.FEED_LINE);
      }
      
      // Selected options/modifiers - show ALL for kitchen (including non-priced)
      if (item.selected_options?.length > 0) {
        commands.push(...COMMANDS.BOLD_OFF);
        for (const opt of item.selected_options) {
          const priceStr = opt.price > 0 ? ` (+${this.formatCurrency(opt.price)})` : '';
          commands.push(...this.textToBytes(`   + ${opt.option_name}${priceStr}`));
          commands.push(...COMMANDS.FEED_LINE);
        }
        commands.push(...COMMANDS.BOLD_ON);
      }
      
      // Meal slot selections (for combo meals)
      if (item.meal_selections?.length > 0) {
        commands.push(...COMMANDS.BOLD_OFF);
        for (const slot of item.meal_selections) {
          commands.push(...this.textToBytes(`   ${slot.slot_name}: ${slot.product_name}`));
          commands.push(...COMMANDS.FEED_LINE);
          if (slot.prep_instructions) {
            commands.push(...this.textToBytes(`      * ${slot.prep_instructions}`));
            commands.push(...COMMANDS.FEED_LINE);
          }
        }
        commands.push(...COMMANDS.BOLD_ON);
      }
      
      // General notes
      if (item.notes) {
        commands.push(...COMMANDS.BOLD_OFF);
        commands.push(...this.textToBytes(`   >> ${item.notes}`));
        commands.push(...COMMANDS.BOLD_ON);
        commands.push(...COMMANDS.FEED_LINE);
      }
      
      // Legacy modifiers array (backwards compatibility)
      if (item.modifiers?.length > 0 && !item.selected_options?.length) {
        commands.push(...COMMANDS.BOLD_OFF);
        for (const mod of item.modifiers) {
          commands.push(...this.textToBytes(`   + ${mod}`));
          commands.push(...COMMANDS.FEED_LINE);
        }
        commands.push(...COMMANDS.BOLD_ON);
      }
    }
    commands.push(...COMMANDS.BOLD_OFF);

    commands.push(...this.textToBytes(this.dividerLine('=', width)));
    commands.push(...COMMANDS.FEED_LINE);

    // Special instructions
    if (ticketData.specialInstructions) {
      commands.push(...COMMANDS.BOLD_ON);
      commands.push(...this.textToBytes('SPECIAL INSTRUCTIONS:'));
      commands.push(...COMMANDS.FEED_LINE);
      commands.push(...COMMANDS.BOLD_OFF);
      commands.push(...this.textToBytes(ticketData.specialInstructions));
      commands.push(...COMMANDS.FEED_LINE);
      commands.push(...this.textToBytes(this.dividerLine('-', width)));
      commands.push(...COMMANDS.FEED_LINE);
    }

    // Rush order indicator
    if (ticketData.isRush) {
      commands.push(...COMMANDS.ALIGN_CENTER);
      commands.push(...COMMANDS.INVERSE_ON);
      commands.push(...COMMANDS.DOUBLE_SIZE);
      commands.push(...this.textToBytes(' !!! RUSH ORDER !!! '));
      commands.push(...COMMANDS.INVERSE_OFF);
      commands.push(...COMMANDS.NORMAL_SIZE);
      commands.push(...COMMANDS.FEED_LINE);
    }

    // Reprint notice
    if (ticketData.isReprint) {
      commands.push(...COMMANDS.ALIGN_CENTER);
      commands.push(...COMMANDS.BOLD_ON);
      commands.push(...this.textToBytes('*** REPRINT ***'));
      commands.push(...COMMANDS.BOLD_OFF);
      commands.push(...COMMANDS.FEED_LINE);
    }

    // Feed and cut
    commands.push(...COMMANDS.FEED_LINES(3));
    commands.push(...COMMANDS.CUT_FEED_PARTIAL);

    await this.writeRaw(printerId, commands);

    return { success: true, printerId };
  }

  /**
   * Print to first available printer of a specific type
   */
  async printToType(type, printFunction, data) {
    const printer = this.getPrinterByType(type);
    if (!printer) {
      throw new Error(`No ${type} printer connected`);
    }
    return printFunction.call(this, printer.id, data);
  }

  /**
   * Open cash drawer (if connected to receipt printer)
   */
  async openCashDrawer(printerId) {
    await this.writeRaw(printerId || this.getConnectedPrinters()[0]?.id, COMMANDS.OPEN_DRAWER);
  }

  /**
   * Test print - prints a test page
   */
  async testPrint(printerId) {
    const connection = this.connectedPrinters.get(printerId);
    if (!connection) {
      throw new Error('Printer not connected');
    }

    const width = connection.paperWidth === 80 ? 48 : 32;
    const commands = [];

    commands.push(...COMMANDS.INIT);
    commands.push(...COMMANDS.ALIGN_CENTER);
    commands.push(...COMMANDS.DOUBLE_SIZE);
    commands.push(...COMMANDS.BOLD_ON);
    commands.push(...this.textToBytes('PRINTER TEST'));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...COMMANDS.NORMAL_SIZE);
    commands.push(...COMMANDS.BOLD_OFF);
    
    commands.push(...this.textToBytes(this.dividerLine('-', width)));
    commands.push(...COMMANDS.FEED_LINE);
    
    commands.push(...COMMANDS.ALIGN_LEFT);
    commands.push(...this.textToBytes(`Printer: ${connection.name}`));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...this.textToBytes(`Paper Width: ${connection.paperWidth}mm`));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...this.textToBytes(`Characters: ${width}`));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...this.textToBytes(`Time: ${new Date().toLocaleString()}`));
    commands.push(...COMMANDS.FEED_LINE);
    
    commands.push(...this.textToBytes(this.dividerLine('-', width)));
    commands.push(...COMMANDS.FEED_LINE);
    
    commands.push(...COMMANDS.ALIGN_CENTER);
    commands.push(...this.textToBytes('HostPoint POS'));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...this.textToBytes('Printer Test Successful!'));
    commands.push(...COMMANDS.FEED_LINE);
    
    commands.push(...COMMANDS.FEED_LINES(3));
    commands.push(...COMMANDS.CUT_FEED_PARTIAL);

    await this.writeRaw(printerId, commands);

    return { success: true };
  }
}

// Export singleton instance
export const printerManager = new BluetoothPrinterManager();
export default printerManager;
